import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import {
  getCanonicalUrl,
  getPageMeta,
  getRobotsContent,
  normalizePathname,
} from "@/vike/pageMetadata";
import { BASE_URL } from "@/vike/schema/constants";
import { isServicePath, isWorkerPath } from "@/shared/publicPaths";

function upsertMetaByName(name: string, content: string) {
  let el = document.querySelector(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("name", name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertMetaByProperty(property: string, content: string) {
  let el = document.querySelector(`meta[property="${property}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("property", property);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertCanonical(href: string) {
  let el = document.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function removeKeywordsMeta() {
  document.querySelectorAll('meta[name="keywords"]').forEach((node) => node.remove());
}

function syncDocumentMeta(pathname: string, title: string, description: string) {
  const canonicalUrl = getCanonicalUrl(pathname);
  const robotsContent = getRobotsContent(pathname);
  const ogImageUrl = `${BASE_URL}/jandi_og_1200x630.png`;

  document.title = title;
  upsertMetaByName("description", description);
  upsertMetaByName("robots", robotsContent);
  upsertMetaByProperty("og:title", title);
  upsertMetaByProperty("og:description", description);
  upsertMetaByProperty("og:url", canonicalUrl);
  if (pathname === "/") {
    upsertMetaByProperty("og:image", ogImageUrl);
  }
  upsertCanonical(canonicalUrl);
}

/**
 * Keeps document title + key SEO meta in sync on CSR route changes (SPA under Vike).
 */
export default function SiteDocumentTitleSync() {
  const location = useLocation();

  useEffect(() => {
    if (typeof document === "undefined") return;

    const pathname = normalizePathname(location.pathname);
    document.documentElement.lang = "es-PR";
    removeKeywordsMeta();

    const isDynamicPublicPage = isServicePath(pathname) || isWorkerPath(pathname);

    if (isDynamicPublicPage) {
      const ogTitle =
        document.querySelector('meta[property="og:title"]')?.getAttribute("content") ||
        document.title;
      const ogDescription =
        document.querySelector('meta[property="og:description"]')?.getAttribute("content") ||
        getPageMeta(pathname).description;

      syncDocumentMeta(pathname, ogTitle, ogDescription);
      return;
    }

    const meta = getPageMeta(pathname);
    syncDocumentMeta(pathname, meta.title, meta.description);
  }, [location.pathname, location.search]);

  return null;
}
